// export const createCsvString = (data) => {
//   let csvString = "Category,Item Name,Item Count\n"; // CSV Header
//   Object.entries(data).forEach(([category, items]) => {
//     items.forEach((item) => {
//       csvString += `${category.charAt(0).toUpperCase() + category.slice(1)},${
//         item.description
//       },${item.quantity}\n`;
//     });
//   });
//   return csvString;
// };

// export const downloadCsv = (csvString, filename = "export.csv") => {
//   const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
//   const link = document.createElement("a");
//   link.href = URL.createObjectURL(blob);
//   link.setAttribute("download", filename);
//   document.body.appendChild(link);
//   link.click();
//   document.body.removeChild(link);
// };
import ExcelJS from "exceljs";

export async function exportItemsByCategoryXLSX({
  data, // { loyalists: [{description, quantity}], detractors: [...] }
  storeName = "All Stores",
  dateRange = "Not set",
  fileName = "Customer_Items_By_Category.xlsx",
  title = "Top Products per Customer Category",
  // optional colors per category
  categoryColors = {
    loyalists: "#16a34a",
    potentialLoyalists: "#0891b2",
    promising: "#6366f1",
    hesitant: "#9333ea",
    needAttention: "#ea580c",
    detractors: "#e11d48",
  },
}) {
  if (!data || typeof data !== "object") {
    console.error("exportItemsByCategoryXLSX: invalid data");
    return;
  }

  const toARGB = (hex) => {
    const h = String(hex || "")
      .replace("#", "")
      .trim();
    return h.length === 6 ? `FF${h.toUpperCase()}` : "FF111827";
  };

  const niceLabel = (key) => {
    const map = {
      loyalists: "Loyalists",
      potentialLoyalists: "Potential Loyalists",
      promising: "Promising",
      hesitant: "Hesitant",
      needAttention: "Need Attention",
      detractors: "Detractors",
    };
    return map[key] || key;
  };

  const setBorder = (cell, thick = false) => {
    const style = thick ? "medium" : "thin";
    cell.border = {
      top: { style },
      left: { style },
      bottom: { style },
      right: { style },
    };
  };

  const workbook = new ExcelJS.Workbook();
  workbook.creator = "Analytics Export";
  workbook.created = new Date();

  const sheet = workbook.addWorksheet("By Category", {
    views: [{ state: "frozen", ySplit: 4 }],
  });

  // columns
  sheet.getColumn(1).width = 40; // Item Name
  sheet.getColumn(2).width = 14; // Item Count

  // ===== Title =====
  sheet.mergeCells("A1:B1");
  const t = sheet.getCell("A1");
  t.value = title;
  t.font = { bold: true, size: 16, color: { argb: "FFFFFFFF" } };
  t.alignment = { vertical: "middle", horizontal: "left" };
  t.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF1F2937" } };
  sheet.getRow(1).height = 26;

  // Meta
  sheet.mergeCells("A2:B2");
  const m1 = sheet.getCell("A2");
  m1.value = `Store: ${storeName}`;
  m1.font = { size: 11, italic: true, color: { argb: "FF111827" } };
  m1.fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "FFF3F4F6" },
  };

  sheet.mergeCells("A3:B3");
  const m2 = sheet.getCell("A3");
  m2.value = `Date Range: ${dateRange}`;
  m2.font = { size: 11, italic: true, color: { argb: "FF111827" } };
  m2.fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "FFF3F4F6" },
  };

  sheet.getRow(4).height = 8;

  // ===== Build separate blocks per category =====
  // Keep ordering consistent
  const order = [
    "loyalists",
    "potentialLoyalists",
    "promising",
    "hesitant",
    "needAttention",
    "detractors",
  ];

  let rowCursor = 5;

  for (const key of order) {
    const items = Array.isArray(data[key]) ? data[key] : [];
    if (!items.length) continue;

    const catName = niceLabel(key);
    const catColor = toARGB(categoryColors[key] || "#111827");

    // Category title row (merged)
    sheet.mergeCells(`A${rowCursor}:B${rowCursor}`);
    const catCell = sheet.getCell(`A${rowCursor}`);
    catCell.value = catName.toUpperCase();
    catCell.font = { bold: true, size: 12, color: { argb: "FFFFFFFF" } };
    catCell.alignment = { vertical: "middle", horizontal: "left" };
    catCell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: catColor },
    };
    sheet.getRow(rowCursor).height = 20;
    setBorder(catCell, true);
    setBorder(sheet.getCell(`B${rowCursor}`), true);

    rowCursor++;

    // Header row for the table
    const h1 = sheet.getCell(`A${rowCursor}`);
    const h2 = sheet.getCell(`B${rowCursor}`);
    h1.value = "Item Name";
    h2.value = "Item Count";

    [h1, h2].forEach((c) => {
      c.font = { bold: true, color: { argb: "FFFFFFFF" } };
      c.alignment = { vertical: "middle", horizontal: "center" };
      c.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FF2F2F2F" },
      };
      setBorder(c, true);
    });
    sheet.getRow(rowCursor).height = 18;

    const firstDataRow = rowCursor + 1;
    rowCursor++;

    // Data rows
    let total = 0;

    items.forEach((it, idx) => {
      const name = it?.description ?? "";
      const qty = Number(it?.quantity || 0);

      total += qty;

      const c1 = sheet.getCell(`A${rowCursor}`);
      const c2 = sheet.getCell(`B${rowCursor}`);

      c1.value = name;
      c2.value = qty;

      c1.alignment = { vertical: "middle", horizontal: "left" };
      c2.alignment = { vertical: "middle", horizontal: "center" };
      c2.numFmt = "#,##0";

      // zebra + borders
      const isAlt = idx % 2 === 1;
      if (isAlt) {
        c1.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FFF9FAFB" },
        };
        c2.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FFF9FAFB" },
        };
      }

      setBorder(c1, false);
      setBorder(c2, false);

      rowCursor++;
    });

    const lastDataRow = rowCursor - 1;

    // Total row
    const tot1 = sheet.getCell(`A${rowCursor}`);
    const tot2 = sheet.getCell(`B${rowCursor}`);

    tot1.value = "TOTAL";
    tot2.value = { formula: `SUM(B${firstDataRow}:B${lastDataRow})` };

    [tot1, tot2].forEach((c) => {
      c.font = { bold: true, color: { argb: "FFFFFFFF" } };
      c.alignment = { vertical: "middle", horizontal: "center" };
      c.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FF6DA544" },
      };
      setBorder(c, true);
    });
    tot2.numFmt = "#,##0";
    sheet.getRow(rowCursor).height = 20;

    rowCursor += 2; // space between category tables
  }

  // ===== Download =====
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });

  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  setTimeout(() => URL.revokeObjectURL(link.href), 500);
}
